import { FC } from 'react';

import { useFormik } from 'formik';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { SinglePromoCodeForm, singlePromoCodeFormInitialValues } from '../form';
import { PromoCodeRewardPicker } from '../promoCodeRewardPicker/PromoCodeRewardPicker';
import { buildPromoCodePayload, getPromoCodeCountByType } from '../utils';

import { promoCodeService } from 'v2/api/promoCodeService';
import { PromoCodeType } from 'v2/constants';
import { handleHttpError } from 'v2/utils';

interface AddUniquePromoCodeDialogProps {
  isOpen: boolean;
  onClose: VoidFunction;
  onRevalidate: VoidFunction;
}

export const AddSinglePromoCodeDialog: FC<AddUniquePromoCodeDialogProps> = ({ isOpen, onClose, onRevalidate }) => {
  const { trigger, isMutating } = useSWRMutation('createPromoCode', promoCodeService.createPromoCode, {
    onSuccess: () => {
      onRevalidate();
      toast.success('Promo code has been created successfully');
      handleClose();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<SinglePromoCodeForm>({
    initialValues: singlePromoCodeFormInitialValues,
    onSubmit: (values) => {
      trigger(buildPromoCodePayload(values));
    },
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  const handleRewardTypeChange = (value: PromoCodeType) => {
    formik.setFieldValue('type', value);
    formik.setFieldValue('value', '');
    formik.setFieldValue('count', getPromoCodeCountByType(value));
  };

  const handleRewardValueChange = (value: string) => {
    formik.setFieldValue('value', value);
  };

  const handleRewardCountChange = (value?: number) => {
    formik.setFieldValue('count', value);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Single Promo Code</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <TextField
            name="groupName"
            value={formik.values.groupName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Group Name"
            required
          />

          <TextField
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Promo Code"
            required
          />

          <PromoCodeRewardPicker
            type={formik.values.type}
            onTypeChange={handleRewardTypeChange}
            value={formik.values.value}
            onValueChange={handleRewardValueChange}
            count={formik.values.count}
            onCountChange={handleRewardCountChange}
          />

          <TextField
            name="activationNumber"
            value={formik.values.activationNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Number Of Activations"
            type="number"
            required
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button loading={isMutating} type="submit" variant="contained">
            Add
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
